/**
 * Skeleton primitives (Phase 1).
 *
 * Pattern from production OnboardingSkeleton.tsx + DashboardSkeleton.tsx
 * carried into preview /preview/dashboard/states/. Renders animate-pulse
 * rectangles with opacity tiers so the loading state visually echoes the
 * real layout (no jump on data land).
 *
 * Brand sheet §4 skeleton rules:
 *   - bg-dark/5 for ambient (light placeholders)
 *   - bg-dark/10 for prominent placeholders
 *   - bg-brand/30 for CTA-shaped placeholders (preserves coral rhythm)
 *
 * Always wrap a group of skeletons in `<SkeletonGroup>` for the correct
 * aria-busy + aria-live attributes (screen readers announce loading).
 */

import type { ReactNode } from 'react';

type Tone = 'ambient' | 'prominent' | 'cta';

const TONE_CLASS: Record<Tone, string> = {
  ambient: 'bg-dark/5',
  prominent: 'bg-dark/10',
  cta: 'bg-brand/30',
};

interface SkeletonBoxProps {
  /** Tailwind classes for width / height / rounded etc. */
  className?: string;
  /** Visual weight. Default = ambient. */
  tone?: Tone;
}

/** Plain rectangle placeholder. */
export function SkeletonBox({ className, tone = 'ambient' }: SkeletonBoxProps) {
  return (
    <div
      aria-hidden="true"
      className={`animate-pulse rounded ${TONE_CLASS[tone]} ${className ?? ''}`}
    />
  );
}

/** Single line of text placeholder (h-4 default). */
export function SkeletonText({
  className,
  tone = 'ambient',
}: SkeletonBoxProps) {
  return <SkeletonBox tone={tone} className={`h-4 ${className ?? 'w-full'}`} />;
}

/** Square avatar-like placeholder. */
export function SkeletonAvatar({
  size = 8,
  tone = 'prominent',
}: {
  /** Tailwind size unit (8 = h-8 w-8). */
  size?: 8 | 10 | 12 | 16;
  tone?: Tone;
}) {
  const sizeClass = `h-${size} w-${size}`;
  return (
    <div
      aria-hidden="true"
      className={`shrink-0 animate-pulse rounded-lg ${TONE_CLASS[tone]} ${sizeClass}`}
    />
  );
}

interface SkeletonGroupProps {
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a group of skeleton elements with aria-busy + aria-live so
 * screen readers announce the loading state correctly. Use this around
 * any page-level skeleton.
 */
export function SkeletonGroup({ children, className }: SkeletonGroupProps) {
  return (
    <div
      aria-busy="true"
      aria-live="polite"
      className={className ?? 'space-y-4'}
    >
      {children}
    </div>
  );
}
